import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import type {
  ComfortBidsParams,
  ReverseAuctionsReportParams,
} from 'src/apis/monolith/types';
import { useAuctionTiles } from 'src/apis/monolith/use-auction-tiles';
import { useCalendars } from 'src/apis/monolith/use-calendars';
import { useOptionalDayAheadComfortBids } from 'src/apis/monolith/use-day-ahead-comfort-bids';
import { useReverseAuctions } from 'src/apis/monolith/use-reverse-auctions';
import type { SchedulesParams } from 'src/apis/transport-auction/types';
import { useAuctionTiles as useAuctionTilesTAS } from 'src/apis/transport-auction/use-auction-tiles';
import { useSchedules } from 'src/apis/transport-auction/use-schedules';
import { useOptionalTransportAuctionServiceRoot } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useOptionalTtpcModulationService } from 'src/apis/ttpc-modulation-service/use-ttpc-modulation-service';
import { PageSpinner } from 'src/components/spinner-container';
import { useOptionalBidCollectorRoot } from 'src/pages/snam-bid-collector/use-bid-collector';
import { useTransportAuctionsPageParams } from 'src/pages/transport/auctions/use-page-params';

const Page = lazy(() => import('./page'));

export const AuctionsStart: FC = () => {
  const pageParams = useTransportAuctionsPageParams();

  const auctionTiles = useAuctionTiles();
  const transportAuctionServiceRoot = useOptionalTransportAuctionServiceRoot();
  const auctionTilesTAS = useAuctionTilesTAS({
    root: transportAuctionServiceRoot,
  });
  const snamBidCollector = useOptionalBidCollectorRoot();
  const ttpcModulationService = useOptionalTtpcModulationService();
  const calendars = useCalendars();

  const schedulesParams = useMemo(
    () =>
      ({
        limit: pageParams.schedules.value.pageSize,
        offset: pageParams.schedules.value.start,
        productType: [pageParams.schedules.value.productType],
        publicationDateStart: pageParams.schedules.value.publicationDateStart,
        publicationDateEnd: pageParams.schedules.value.publicationDateEnd,
        openDateStart: pageParams.schedules.value.openDateStart,
        openDateEnd: pageParams.schedules.value.openDateEnd,
        runtimeIntersectionFilterStart:
          pageParams.schedules.value.runtimeIntersectionFilterStart,
        runtimeIntersectionFilterEnd:
          pageParams.schedules.value.runtimeIntersectionFilterEnd,
        slotIndex: pageParams.schedules.value.slotIndex,
        sortBy: pageParams.schedules.value.sortBy,
        sortDirection: pageParams.schedules.value.sortDirection,
      }) satisfies SchedulesParams,
    [pageParams.schedules.value]
  );
  const schedules = useSchedules({
    root: transportAuctionServiceRoot,
    params: schedulesParams,
  });

  const comfortBidsParams = useMemo(
    () =>
      ({
        pageSize: pageParams.comfortBids.value.pageSize,
        start: pageParams.comfortBids.value.start,
      }) satisfies ComfortBidsParams,
    [pageParams.comfortBids.value.pageSize, pageParams.comfortBids.value.start]
  );
  const comfortBids = useOptionalDayAheadComfortBids({
    params: comfortBidsParams,
  });

  const reverseAuctionsParams = useMemo<ReverseAuctionsReportParams>(
    () => ({
      pageSize: pageParams.reverseAuctions.value.pageSize,
      start: pageParams.reverseAuctions.value.start,
      sortColumn: pageParams.reverseAuctions.value.sortColumn,
      sortAscending: pageParams.reverseAuctions.value.sortDirection === 'asc',
    }),
    [
      pageParams.reverseAuctions.value.pageSize,
      pageParams.reverseAuctions.value.start,
      pageParams.reverseAuctions.value.sortColumn,
      pageParams.reverseAuctions.value.sortDirection,
    ]
  );
  const reverseAuctions = useReverseAuctions({ params: reverseAuctionsParams });

  if (
    transportAuctionServiceRoot?.loaded === false ||
    auctionTilesTAS?.response === null ||
    schedules?.loaded === false ||
    !auctionTiles.response ||
    !snamBidCollector.loaded ||
    !calendars.response ||
    !reverseAuctions.response ||
    comfortBids?.response === null ||
    (PRISMA_CONFIG.ttpcModulationService && !ttpcModulationService.loaded)
  )
    return <PageSpinner />;

  return (
    <Page
      auctionTiles={auctionTiles}
      auctionTilesTAS={auctionTilesTAS}
      schedules={schedules}
      snamBidCollector={snamBidCollector}
      calendars={calendars}
      pageParams={pageParams}
      reverseAuctions={reverseAuctions}
      comfortBids={comfortBids}
      ttpcModulationService={ttpcModulationService}
    />
  );
};
